import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { useTranslation, Trans } from "react-i18next";
import { intlLocale } from "@/lib/locale";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Car, MessageSquare, Send, ArrowLeft, Building2, Trash2, MapPin, Star, ListChecks, CheckCircle2, Pencil } from "lucide-react";
import type { MarketplaceConversationWithDetails, MarketplaceMessage, MarketplaceReview, User } from "@shared/schema";
import { DealerProfileDialog } from "@/components/marketplace/DealerProfileDialog";

type BuyerStatus = {
  hasContacted: boolean;
  existingReview: MarketplaceReview | null;
};

function StarPicker({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`star-${n}`}
          disabled={disabled}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          className="p-0.5 transition-transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("marketplace:messages.starAria", { count: n })}
        >
          <Star
            className={`w-7 h-7 transition-colors ${
              n <= (hovered || value)
                ? "fill-amber-400 text-amber-400"
                : "text-muted-foreground/30"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function LeaveReviewDialog({
  listingId,
  sellerName,
  open,
  onClose,
  onSubmitted,
}: {
  listingId: string;
  sellerName: string;
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const { toast } = useToast();
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/marketplace/${listingId}/review`, {
        rating,
        reviewText: reviewText.trim() || null,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || t("marketplace:messagesToast.failedSubmit"));
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: t("marketplace:messagesToast.reviewSubmitted"), description: t("marketplace:messagesToast.reviewSubmittedDesc") });
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace", listingId, "buyer-status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/conversations"] });
      setRating(0);
      setReviewText("");
      onSubmitted();
      onClose();
    },
    onError: (err: Error) => {
      toast({ title: t("marketplace:messagesToast.couldNotSubmit"), description: err.message, variant: "destructive" });
    },
  });

  function handleClose() {
    if (submitMutation.isPending) return;
    setRating(0);
    setReviewText("");
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm" data-testid="dialog-leave-review">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-400 shrink-0" />
            {t("marketplace:messagesReviewDialog.leaveTitle")}
          </DialogTitle>
          <DialogDescription>
            <Trans i18nKey="marketplace:messagesReviewDialog.leaveDescription" values={{ name: sellerName }}>
              Rate your experience with <span className="font-medium text-foreground">{sellerName}</span>
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <p className="text-sm font-medium">{t("marketplace:messagesReviewDialog.rating")}</p>
            <StarPicker value={rating} onChange={setRating} disabled={submitMutation.isPending} />
            {rating > 0 && (
              <p className="text-xs text-muted-foreground">
                {rating === 1 ? t("marketplace:messagesReviewDialog.ratingPoor") : rating === 2 ? t("marketplace:messagesReviewDialog.ratingFair") : rating === 3 ? t("marketplace:messagesReviewDialog.ratingGood") : rating === 4 ? t("marketplace:messagesReviewDialog.ratingVeryGood") : t("marketplace:messagesReviewDialog.ratingExcellent")}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <p className="text-sm font-medium">{t("marketplace:messagesReviewDialog.comment")} <span className="text-muted-foreground font-normal">{t("marketplace:messagesReviewDialog.optional")}</span></p>
            <Textarea
              data-testid="input-review-text"
              placeholder={t("marketplace:messagesReviewDialog.placeholder")}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              maxLength={2000}
              rows={3}
              disabled={submitMutation.isPending}
              className="resize-none text-sm"
            />
            <p className="text-[10px] text-muted-foreground/50 text-right">{reviewText.length}/2000</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submitMutation.isPending}
            data-testid="button-cancel-review"
          >
            {t("marketplace:messagesReviewDialog.cancel")}
          </Button>
          <Button
            onClick={() => submitMutation.mutate()}
            disabled={rating === 0 || submitMutation.isPending}
            data-testid="button-submit-review"
            className="bg-violet-500 hover:bg-violet-600"
          >
            {submitMutation.isPending ? t("marketplace:messagesReviewDialog.submitting") : t("marketplace:messagesReviewDialog.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditReviewDialog({
  listingId,
  existingReview,
  sellerName,
  open,
  onClose,
  onUpdated,
}: {
  listingId: string;
  existingReview: MarketplaceReview;
  sellerName: string;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const { toast } = useToast();
  const [rating, setRating] = useState(existingReview.rating);
  const [reviewText, setReviewText] = useState(existingReview.reviewText ?? "");

  useEffect(() => {
    if (open) {
      setRating(existingReview.rating);
      setReviewText(existingReview.reviewText ?? "");
    }
  }, [open, existingReview.rating, existingReview.reviewText]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", `/api/marketplace/${listingId}/review`, {
        rating,
        reviewText: reviewText.trim() || null,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || t("marketplace:messagesToast.failedUpdate"));
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: t("marketplace:messagesToast.reviewUpdated"), description: t("marketplace:messagesToast.reviewUpdatedDesc") });
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace", listingId, "buyer-status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/conversations"] });
      onUpdated();
      onClose();
    },
    onError: (err: Error) => {
      toast({ title: t("marketplace:messagesToast.couldNotUpdate"), description: err.message, variant: "destructive" });
    },
  });

  function handleClose() {
    if (updateMutation.isPending) return;
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm" data-testid="dialog-edit-review">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-amber-400 shrink-0" />
            {t("marketplace:messagesReviewDialog.editTitle")}
          </DialogTitle>
          <DialogDescription>
            <Trans i18nKey="marketplace:messagesReviewDialog.editDescription" values={{ name: sellerName }}>
              Update your review for <span className="font-medium text-foreground">{sellerName}</span>
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <p className="text-sm font-medium">{t("marketplace:messagesReviewDialog.rating")}</p>
            <StarPicker value={rating} onChange={setRating} disabled={updateMutation.isPending} />
            {rating > 0 && (
              <p className="text-xs text-muted-foreground">
                {rating === 1 ? t("marketplace:messagesReviewDialog.ratingPoor") : rating === 2 ? t("marketplace:messagesReviewDialog.ratingFair") : rating === 3 ? t("marketplace:messagesReviewDialog.ratingGood") : rating === 4 ? t("marketplace:messagesReviewDialog.ratingVeryGood") : t("marketplace:messagesReviewDialog.ratingExcellent")}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <p className="text-sm font-medium">{t("marketplace:messagesReviewDialog.comment")} <span className="text-muted-foreground font-normal">{t("marketplace:messagesReviewDialog.optional")}</span></p>
            <Textarea
              data-testid="input-edit-review-text"
              placeholder={t("marketplace:messagesReviewDialog.placeholder")}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              maxLength={2000}
              rows={3}
              disabled={updateMutation.isPending}
              className="resize-none text-sm"
            />
            <p className="text-[10px] text-muted-foreground/50 text-right">{reviewText.length}/2000</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={updateMutation.isPending}
            data-testid="button-cancel-edit-review"
          >
            {t("marketplace:messagesReviewDialog.cancel")}
          </Button>
          <Button
            onClick={() => updateMutation.mutate()}
            disabled={rating === 0 || updateMutation.isPending}
            data-testid="button-save-review"
            className="bg-violet-500 hover:bg-violet-600"
          >
            {updateMutation.isPending ? t("marketplace:messagesReviewDialog.saving") : t("marketplace:messagesReviewDialog.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function formatRelative(
  date: Date | string | null | undefined,
  t: (key: string, opts?: any) => string,
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return t("marketplace:messages.justNow");
  if (diffMins < 60) return t("marketplace:messages.minutesAgo", { count: diffMins });
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return t("marketplace:messages.hoursAgo", { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return t("marketplace:messages.daysAgo", { count: diffDays });
  return d.toLocaleDateString(intlLocale(), { month: "short", day: "numeric" });
}

function ConversationRow({
  conv,
  isActive,
  myUserId,
  onClick,
}: {
  conv: MarketplaceConversationWithDetails;
  isActive: boolean;
  myUserId: string;
  onClick: () => void;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const lastIsMe = conv.lastMessageSenderUserId === myUserId;

  return (
    <button
      type="button"
      data-testid={`conv-row-${conv.id}`}
      onClick={onClick}
      className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border transition-all ${
        isActive
          ? "border-violet-500/50 bg-violet-500/5"
          : "border-transparent hover:bg-muted/50"
      }`}
    >
      {/* Vehicle thumbnail */}
      <div className="w-14 h-10 rounded-md bg-muted overflow-hidden shrink-0 flex items-center justify-center">
        {conv.listingHeroPhoto ? (
          <img src={conv.listingHeroPhoto} alt="" className="w-full h-full object-cover" />
        ) : (
          <Car className="w-5 h-5 text-muted-foreground/40" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-1">
          <p className="text-sm font-medium truncate" data-testid={`conv-listing-title-${conv.id}`}>
            {conv.listingTitle}
          </p>
          {conv.unreadCount > 0 && (
            <span
              data-testid={`conv-unread-badge-${conv.id}`}
              className="shrink-0 ml-1 min-w-[18px] h-[18px] rounded-full bg-violet-500 text-white text-[10px] font-bold flex items-center justify-center px-1"
            >
              {conv.unreadCount > 99 ? "99+" : conv.unreadCount}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
          <Building2 className="w-3 h-3 shrink-0" />
          <span className="truncate">{conv.otherPartyName}</span>
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1 truncate" data-testid={`conv-last-msg-${conv.id}`}>
          {conv.lastMessageBody
            ? `${lastIsMe ? t("marketplace:messages.youPrefix") : ""}${conv.lastMessageBody}`
            : t("marketplace:messages.conversationStarted")}
        </p>
      </div>

      {conv.lastMessageAt && (
        <span className="text-[10px] text-muted-foreground/60 shrink-0 mt-0.5">
          {formatRelative(conv.lastMessageAt, t)}
        </span>
      )}
    </button>
  );
}

function ThreadView({
  conversationId,
  conversation,
  myUserId,
  onDeleted,
}: {
  conversationId: string;
  conversation: MarketplaceConversationWithDetails | null;
  myUserId: string;
  onDeleted: () => void;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const { toast } = useToast();
  const [draft, setDraft] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDealerProfile, setShowDealerProfile] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showEditReviewDialog, setShowEditReviewDialog] = useState(false);
  const [showDeleteReviewConfirm, setShowDeleteReviewConfirm] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const listingId = conversation?.listingId ?? "";
  const isBuyer = !!conversation && myUserId === conversation.buyerUserId;
  const isReviewableStatus =
    !!conversation &&
    (conversation.listingStatus === "sold" || conversation.listingStatus === "pending_sale");
  const shouldFetchBuyerStatus = isBuyer && isReviewableStatus && !!listingId;

  const { data: buyerStatus, refetch: refetchBuyerStatus } = useQuery<BuyerStatus>({
    queryKey: ["/api/marketplace", listingId, "buyer-status"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/marketplace/${listingId}/buyer-status`);
      return res.json();
    },
    enabled: shouldFetchBuyerStatus,
  });

  const alreadyReviewed = !!buyerStatus?.existingReview;

  const { data: messages = [], isLoading } = useQuery<MarketplaceMessage[]>({
    queryKey: ["/api/marketplace/conversations", conversationId, "messages"],
    queryFn: async () => {
      await apiRequest("POST", `/api/marketplace/conversations/${conversationId}/messages/mark-read`);
      const res = await apiRequest("GET", `/api/marketplace/conversations/${conversationId}/messages`);
      return res.json();
    },
    refetchInterval: 20000,
  });

  // Scroll to bottom when messages load or update
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Invalidate unread count whenever we view a thread
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["/api/marketplace/unread-count"] });
    queryClient.invalidateQueries({ queryKey: ["/api/marketplace/conversations"] });
  }, [conversationId]);

  const sendMutation = useMutation({
    mutationFn: async (body: string) => {
      const res = await apiRequest("POST", `/api/marketplace/conversations/${conversationId}/messages`, { body });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || t("marketplace:messagesToast.failedSend"));
      }
      return res.json();
    },
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/conversations", conversationId, "messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/conversations"] });
    },
    onError: (err: Error) => {
      toast({ title: t("marketplace:messagesToast.messageNotSent"), description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/marketplace/conversations/${conversationId}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || t("marketplace:messagesToast.failedDelete"));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/unread-count"] });
      setShowDeleteConfirm(false);
      onDeleted();
    },
    onError: (err: Error) => {
      toast({ title: t("marketplace:messagesToast.couldNotDelete"), description: err.message, variant: "destructive" });
    },
  });

  const deleteReviewMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/marketplace/${listingId}/review`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || t("marketplace:messagesToast.failedDeleteReview"));
      }
    },
    onSuccess: () => {
      toast({ title: t("marketplace:messagesToast.reviewDeleted"), description: t("marketplace:messagesToast.reviewDeletedDesc") });
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace", listingId, "buyer-status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/conversations"] });
      setShowDeleteReviewConfirm(false);
    },
    onError: (err: Error) => {
      toast({ title: t("marketplace:messagesToast.couldNotDeleteReview"), description: err.message, variant: "destructive" });
    },
  });

  function handleSend() {
    const trimmed = draft.trim();
    if (!trimmed || sendMutation.isPending) return;
    sendMutation.mutate(trimmed);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <Link
            href={conversation ? `/marketplace/${conversation.listingId}?back=${encodeURIComponent(`/marketplace/messages?conv=${conversationId}`)}` : "/marketplace"}
            data-testid="link-thread-listing-photo"
            className="w-10 h-8 rounded bg-muted overflow-hidden shrink-0 flex items-center justify-center hover:opacity-80 transition-opacity"
            title={conversation?.listingTitle}
          >
            {conversation?.listingHeroPhoto ? (
              <img src={conversation.listingHeroPhoto} alt="" className="w-full h-full object-cover" />
            ) : (
              <Car className="w-4 h-4 text-muted-foreground/40" />
            )}
          </Link>
          <div className="min-w-0 flex-1">
            {conversation ? (
              <>
                <Link
                  href={`/marketplace/${conversation.listingId}?back=${encodeURIComponent(`/marketplace/messages?conv=${conversationId}`)}`}
                  data-testid="link-thread-listing-title"
                  className="text-sm font-semibold truncate block hover:text-violet-500 transition-colors"
                >
                  {conversation.listingTitle}
                </Link>
                <button
                  type="button"
                  data-testid="button-view-dealer-profile"
                  onClick={() => setShowDealerProfile(true)}
                  className="text-xs text-muted-foreground flex items-center gap-1 hover:text-violet-500 transition-colors w-fit"
                >
                  <Building2 className="w-3 h-3 shrink-0" />
                  <span className="underline underline-offset-2 decoration-dashed">{conversation.otherPartyName}</span>
                </button>
              </>
            ) : (
              <>
                <Skeleton className="h-4 w-40 mb-1" />
                <Skeleton className="h-3 w-24" />
              </>
            )}
          </div>
          {/* Delete conversation */}
          <Button
            variant="ghost"
            size="icon"
            data-testid="button-delete-conversation"
            className="shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => setShowDeleteConfirm(true)}
            title={t("marketplace:messages.deleteConversationTitle")}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Review CTA banner — shown to buyer when listing is sold/pending_sale */}
      {shouldFetchBuyerStatus && (
        <div
          data-testid="review-cta-banner"
          className="px-4 py-2.5 border-b border-border shrink-0 bg-amber-50 dark:bg-amber-950/20 flex items-center justify-between gap-3"
        >
          {alreadyReviewed ? (
            <div className="flex items-center justify-between w-full gap-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>{t("marketplace:messages.youReviewed")}</span>
                {buyerStatus?.existingReview && (
                  <span className="flex items-center gap-0.5 ml-1">
                    {Array.from({ length: buyerStatus.existingReview.rating }).map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                    ))}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="button-edit-review"
                  className="h-7 px-2.5 text-xs gap-1"
                  onClick={() => setShowEditReviewDialog(true)}
                >
                  <Pencil className="w-3 h-3" />
                  {t("marketplace:messages.edit")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="button-delete-review"
                  className="h-7 px-2.5 text-xs gap-1 text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/60 hover:bg-destructive/5"
                  onClick={() => setShowDeleteReviewConfirm(true)}
                >
                  <Trash2 className="w-3 h-3" />
                  {t("marketplace:messages.delete")}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm">
                <Star className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="text-foreground font-medium">{t("marketplace:messages.dealComplete")}</span>
                <span className="text-muted-foreground hidden sm:inline">{t("marketplace:messages.leaveReviewForSeller")}</span>
              </div>
              <Button
                size="sm"
                data-testid="button-leave-review"
                className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white h-7 px-3 text-xs"
                onClick={() => setShowReviewDialog(true)}
              >
                {t("marketplace:messages.leaveReview")}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Delete confirm dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm" data-testid="dialog-delete-confirm">
          <DialogHeader>
            <DialogTitle>{t("marketplace:messages.deleteConfirmTitle")}</DialogTitle>
            <DialogDescription>
              {t("marketplace:messages.deleteConfirmDesc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} data-testid="button-cancel-delete">
              {t("marketplace:messages.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? t("marketplace:messages.deleting") : t("marketplace:messages.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dealer profile popup */}
      {conversation && (
        <DealerProfileDialog
          userId={conversation.otherPartyUserId}
          displayName={conversation.otherPartyName}
          open={showDealerProfile}
          onClose={() => setShowDealerProfile(false)}
        />
      )}

      {/* Leave review dialog */}
      {conversation && (
        <LeaveReviewDialog
          listingId={conversation.listingId}
          sellerName={conversation.otherPartyName}
          open={showReviewDialog}
          onClose={() => setShowReviewDialog(false)}
          onSubmitted={() => refetchBuyerStatus()}
        />
      )}

      {/* Edit review dialog */}
      {conversation && buyerStatus?.existingReview && (
        <EditReviewDialog
          listingId={conversation.listingId}
          existingReview={buyerStatus.existingReview}
          sellerName={conversation.otherPartyName}
          open={showEditReviewDialog}
          onClose={() => setShowEditReviewDialog(false)}
          onUpdated={() => refetchBuyerStatus()}
        />
      )}

      {/* Delete review confirm dialog */}
      <Dialog open={showDeleteReviewConfirm} onOpenChange={setShowDeleteReviewConfirm}>
        <DialogContent className="max-w-sm" data-testid="dialog-delete-review-confirm">
          <DialogHeader>
            <DialogTitle>{t("marketplace:messages.deleteReviewTitle")}</DialogTitle>
            <DialogDescription>
              {t("marketplace:messages.deleteReviewDesc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteReviewConfirm(false)}
              disabled={deleteReviewMutation.isPending}
              data-testid="button-cancel-delete-review"
            >
              {t("marketplace:messages.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteReviewMutation.mutate()}
              disabled={deleteReviewMutation.isPending}
              data-testid="button-confirm-delete-review"
            >
              {deleteReviewMutation.isPending ? t("marketplace:messages.deleting") : t("marketplace:messages.deleteReview")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-list">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}>
                <Skeleton className="h-10 w-48 rounded-xl" />
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <MessageSquare className="w-10 h-10 text-muted-foreground/20 mb-3" />
            <p className="text-sm text-muted-foreground">{t("marketplace:messages.noMessages")}</p>
            <p className="text-xs text-muted-foreground/60 mt-1">{t("marketplace:messages.startConversation")}</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.senderUserId === myUserId;
            const sentAt = msg.sentAt ? formatRelative(msg.sentAt, t) : "";
            return (
              <div
                key={msg.id}
                data-testid={`msg-${msg.id}`}
                className={`flex ${isMe ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    isMe
                      ? "bg-violet-500 text-white rounded-br-sm"
                      : "bg-muted text-foreground rounded-bl-sm"
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                  <p className={`text-[10px] mt-1 text-right ${isMe ? "text-white/60" : "text-muted-foreground/60"}`}>
                    {sentAt}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      <div className="px-4 py-3 border-t border-border shrink-0">
        <div className="flex gap-2 items-end">
          <Textarea
            ref={textareaRef}
            data-testid="input-message-body"
            placeholder={t("marketplace:messages.typePlaceholder")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            maxLength={5000}
            disabled={sendMutation.isPending}
            className="resize-none text-sm flex-1"
          />
          <Button
            data-testid="button-send-message"
            size="icon"
            onClick={handleSend}
            disabled={!draft.trim() || sendMutation.isPending}
            className="shrink-0 bg-violet-500 hover:bg-violet-600"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground/50 mt-1.5 text-right">{draft.length}/5000</p>
      </div>
    </div>
  );
}

export default function MarketplaceMessagesPage() {
  const { t } = useTranslation(["marketplace", "common"]);
  const [, navigate] = useLocation();
  const searchParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const initialConvId = searchParams.get("conv") ?? null;
  const [activeConvId, setActiveConvId] = useState<string | null>(initialConvId);
  const [showThread, setShowThread] = useState(!!initialConvId);

  const { data: me } = useQuery<Pick<User, "id">>({ queryKey: ["/api/auth/me"], retry: false });
  const myUserId = me?.id ?? "";

  const { data: conversations = [], isLoading } = useQuery<MarketplaceConversationWithDetails[]>({
    queryKey: ["/api/marketplace/conversations"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/marketplace/conversations");
      return res.json();
    },
    refetchInterval: 30000,
  });

  const activeConv = conversations.find((c) => c.id === activeConvId) ?? null;

  function selectConv(conv: MarketplaceConversationWithDetails) {
    setActiveConvId(conv.id);
    setShowThread(true);
    // Update URL without full navigation
    const url = new URL(window.location.href);
    url.searchParams.set("conv", conv.id);
    window.history.replaceState({}, "", url.toString());
  }

  return (
    <div className="flex h-[calc(100vh-57px)] overflow-hidden">
      {/* ── Conversation list panel ── */}
      <div
        className={`w-full md:w-80 lg:w-96 shrink-0 border-r border-border flex flex-col ${
          showThread ? "hidden md:flex" : "flex"
        }`}
      >
        <div className="px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              data-testid="button-back-marketplace"
              className="mr-1 -ml-2"
              onClick={() => navigate("/marketplace")}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h2 className="font-semibold text-sm">{t("marketplace:messages.title")}</h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <MessageSquare className="w-10 h-10 text-muted-foreground/20 mb-3" />
              <p className="text-sm text-muted-foreground font-medium">{t("marketplace:messages.noConversations")}</p>
              <p className="text-xs text-muted-foreground/60 mt-1 max-w-[200px]">
                {t("marketplace:messages.noConversationsHint")}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                data-testid="button-browse-marketplace"
                onClick={() => navigate("/marketplace")}
              >
                {t("marketplace:messages.browseListings")}
              </Button>
            </div>
          ) : (
            conversations.map((conv) => (
              <ConversationRow
                key={conv.id}
                conv={conv}
                isActive={conv.id === activeConvId}
                myUserId={myUserId}
                onClick={() => selectConv(conv)}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Thread panel ── */}
      <div className={`flex-1 flex flex-col overflow-hidden ${showThread ? "flex" : "hidden md:flex"}`}>
        {activeConvId ? (
          <>
            {/* Mobile back button */}
            <div className="md:hidden px-3 py-2 border-b border-border shrink-0">
              <Button
                variant="ghost"
                size="sm"
                data-testid="button-back-convlist"
                onClick={() => setShowThread(false)}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t("marketplace:messages.back")}
              </Button>
            </div>
            <ThreadView
              conversationId={activeConvId}
              conversation={activeConv}
              myUserId={myUserId}
              onDeleted={() => {
                setActiveConvId(null);
                setShowThread(false);
                const url = new URL(window.location.href);
                url.searchParams.delete("conv");
                window.history.replaceState({}, "", url.toString());
              }}
            />
          </>
        ) : (
          /* No active conversation — show empty state and mobile back affordance */
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="md:hidden mb-4">
              <Button
                variant="ghost"
                size="sm"
                data-testid="button-back-convlist-empty"
                onClick={() => setShowThread(false)}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t("marketplace:messages.backToConversations")}
              </Button>
            </div>
            <MessageSquare className="w-12 h-12 text-muted-foreground/20 mb-3" />
            <p className="text-muted-foreground font-medium">{t("marketplace:messages.selectConversation")}</p>
            <p className="text-sm text-muted-foreground/60 mt-1">
              {t("marketplace:messages.selectConversationHint")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
